import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/react-hooks'

import { useAuth0 } from '@/auth/index'
import { Context } from '@/shared/store/reducers/global-reducer'
import { fetchPagQuery } from '@/shared/services/pag-api'
import { GET_AGENCIES } from '@/shared/graphql/queryGetAgencies'
import { GET_PROJECT_REGIONS } from '@/shared/graphql/queryGetProjectRegions'
import { GET_PROJECT_CATEGORIES } from '@/shared/graphql/queryGetProjectCategories'
import { GET_PROJECT_TYPES } from '@/shared/graphql/queryGetProjectTypes'
import { GET_PROJECT_STATUSES } from '@/shared/graphql/queryGetProjectStatus'
import { GET_PROJECT_TEAM_ROLES } from '@/shared/graphql/queryGetProjectTeamRoles'
import { GET_CONTACTS } from '@/shared/graphql/queryGetContacts'
import { GET_CITIES } from '@/shared/graphql/queryGetCities'
import { GET_SHOOT_TYPES } from '@/shared/graphql/queryGetShootTypes'

const LOOKUP_LIMIT = 5000

/** Facets controlled in the slide drawer — values are stable ids in Algolia. */
export const DRAWER_REFINEMENT_ATTRIBUTES = [
  'agencyId',
  'projectRegionId',
  'projectCategoryId',
  'projectTypeId',
  'projectTeamContactIds',
  'projectTeamRoleIds',
  'facetShootCityIds',
  'facetShootTypeIds',
] as const

export const FACET_ID_ATTRIBUTES = new Set<string>([
  ...DRAWER_REFINEMENT_ATTRIBUTES,
  'projectStatusId',
])

export const DRAWER_CHIP_LABELS: Record<string, string> = {
  agencyId: 'Agency',
  projectRegionId: 'Region',
  projectCategoryId: 'Category',
  projectTypeId: 'Type',
  projectTeamContactIds: 'Team member',
  projectTeamRoleIds: 'Team role',
  facetShootCityIds: 'City',
  facetShootTypeIds: 'Shoot type',
  projectStatusId: 'Status',
}

const FACET_ID_NAME_SEP = '\u001f'

export type ProjectsFacetLabelLookup = {
  ready: boolean
  agencies: Record<string, string>
  projectRegions: Record<string, string>
  projectCategories: Record<string, string>
  projectTypes: Record<string, string>
  projectStatuses: Record<string, string>
  projectTeamRoles: Record<string, string>
  contacts: Record<string, string>
  cities: Record<string, string>
  shootTypes: Record<string, string>
}

const EMPTY_LOOKUP: ProjectsFacetLabelLookup = {
  ready: false,
  agencies: {},
  projectRegions: {},
  projectCategories: {},
  projectTypes: {},
  projectStatuses: {},
  projectTeamRoles: {},
  contacts: {},
  cities: {},
  shootTypes: {},
}

export const ProjectsFacetLabelLookupContext = createContext<ProjectsFacetLabelLookup>(EMPTY_LOOKUP)

/** Normalize Algolia / GraphQL ids so `5`, `"5"`, and `"5.0"` share one map key. */
export function normalizeFacetId(raw: string | number | null | undefined): string {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  const n = Number(s)
  if (Number.isFinite(n) && String(n) === s.replace(/\.0+$/, '')) {
    return String(Math.trunc(n))
  }
  return s
}

function toLabelMap(items: any[], labelKey: string): Record<string, string> {
  const map: Record<string, string> = {}
  for (const item of items || []) {
    if (item?.id == null) continue
    const id = normalizeFacetId(item.id)
    if (!id) continue
    const label = item[labelKey]
    map[id] = label != null && String(label).trim() ? String(label).trim() : id
  }
  return map
}

/** Legacy `id\u001fname` / `id|name` token or plain id string → base id. */
export function facetTokenBaseId(token: string): string {
  const s = String(token || '')
  const u = s.indexOf(FACET_ID_NAME_SEP)
  if (u >= 0) return normalizeFacetId(s.slice(0, u))
  const p = s.indexOf('|')
  if (p >= 0) return normalizeFacetId(s.slice(0, p))
  return normalizeFacetId(s)
}

function lookupTableForAttribute(
  attribute: string,
  lookup: ProjectsFacetLabelLookup
): Record<string, string> | null {
  switch (attribute) {
    case 'agencyId':
      return lookup.agencies
    case 'projectRegionId':
      return lookup.projectRegions
    case 'projectCategoryId':
      return lookup.projectCategories
    case 'projectTypeId':
      return lookup.projectTypes
    case 'projectStatusId':
      return lookup.projectStatuses
    case 'projectTeamContactIds':
      return lookup.contacts
    case 'projectTeamRoleIds':
      return lookup.projectTeamRoles
    case 'facetShootCityIds':
      return lookup.cities
    case 'facetShootTypeIds':
      return lookup.shootTypes
    default:
      return null
  }
}

function fallbackLabel(_attribute: string, id: string): string {
  return id
}

/** Resolve an Algolia facet value (id or legacy token) to a display label. */
export function resolveFacetLabel(
  attribute: string,
  token: string,
  lookup: ProjectsFacetLabelLookup | null
): string {
  const s = String(token || '')
  if (!FACET_ID_ATTRIBUTES.has(attribute)) {
    return s
  }
  const baseId = facetTokenBaseId(s)
  const table = lookup?.ready ? lookupTableForAttribute(attribute, lookup) : null
  if (table && baseId && table[baseId]) {
    return table[baseId]
  }
  if (s.includes(FACET_ID_NAME_SEP) || s.includes('|')) {
    const u = s.indexOf(FACET_ID_NAME_SEP)
    if (u >= 0) return s.slice(u + FACET_ID_NAME_SEP.length)
    const p = s.indexOf('|')
    if (p >= 0) return s.slice(p + 1)
  }
  return fallbackLabel(attribute, baseId || s)
}

export function transformIdFacetItems(
  attribute: string,
  items: any[],
  lookup: ProjectsFacetLabelLookup | null
): any[] {
  return (items || []).map((item) => {
    const raw = itemValueToString(item.value ?? item.label ?? '')
    const display = resolveFacetLabel(attribute, raw, lookup)
    return {
      ...item,
      label: display,
      count: typeof item.count === 'number' ? item.count : 0,
    }
  })
}

function itemValueToString(raw: any): string {
  if (raw == null) return ''
  if (Array.isArray(raw)) return itemValueToString(raw[0])
  return String(raw)
}

const LOOKUP_QUERIES = [
  { key: 'agencies', query: GET_AGENCIES, path: ['agencies', 'data'], labelKey: 'name' },
  { key: 'projectRegions', query: GET_PROJECT_REGIONS, path: ['projectRegions', 'data'], labelKey: 'region' },
  {
    key: 'projectCategories',
    query: GET_PROJECT_CATEGORIES,
    path: ['projectCategories', 'data'],
    labelKey: 'category',
  },
  { key: 'projectTypes', query: GET_PROJECT_TYPES, path: ['projectTypes', 'data'], labelKey: 'projectType' },
  {
    key: 'projectStatuses',
    query: GET_PROJECT_STATUSES,
    path: ['projectStatuses', 'data'],
    labelKey: 'status',
  },
  {
    key: 'projectTeamRoles',
    query: GET_PROJECT_TEAM_ROLES,
    path: ['projectTeamRoles', 'data'],
    labelKey: 'role',
  },
  {
    key: 'contacts',
    query: GET_CONTACTS,
    path: ['contacts', 'data'],
    labelKey: 'name',
  },
  { key: 'cities', query: GET_CITIES, path: ['cities', 'data'], labelKey: 'name' },
  { key: 'shootTypes', query: GET_SHOOT_TYPES, path: ['shootTypes', 'data'], labelKey: 'name' },
] as const

function buildLookupQueryVars(
  tenantId: number,
  clientId: number | null | undefined
): Record<string, unknown> {
  const base = { tenantId, offset: 0, limit: LOOKUP_LIMIT }
  if (clientId) {
    return { ...base, clientId: Number(clientId) }
  }
  return base
}

function readPath(data: any, path: string[]): any[] {
  let cur = data
  for (const segment of path) {
    cur = cur?.[segment]
  }
  return Array.isArray(cur) ? cur : []
}

export function ProjectsFacetLabelLookupProvider({
  children,
  clientId: clientIdProp,
}: {
  children: React.ReactNode
  clientId?: number | null
}) {
  const apolloClient = useApolloClient()
  const { getTokenSilently, isAuthenticated, loading: authLoading } = useAuth0()
  const { store } = useContext(Context)
  const tenantId = store.activeTenant?.id
  const effectiveClientId = clientIdProp ?? store.activeClient?.id
  const [lookup, setLookup] = useState<ProjectsFacetLabelLookup>(EMPTY_LOOKUP)
  const loadGenerationRef = useRef(0)
  const getTokenRef = useRef(getTokenSilently)
  getTokenRef.current = getTokenSilently

  useEffect(() => {
    if (!tenantId || authLoading || !isAuthenticated) {
      setLookup(EMPTY_LOOKUP)
      return
    }

    const generation = ++loadGenerationRef.current
    const numericTenantId = Number(tenantId)
    const baseVars = buildLookupQueryVars(numericTenantId, effectiveClientId)

    setLookup({ ...EMPTY_LOOKUP, ready: false })

    const load = async () => {
      try {
        const getToken = () => getTokenRef.current()
        const results = await Promise.allSettled(
          LOOKUP_QUERIES.map((spec) => {
            const vars =
              spec.key === 'contacts' && effectiveClientId
                ? { ...baseVars, clientId: Number(effectiveClientId) }
                : baseVars
            return fetchPagQuery(getToken, apolloClient, spec.query, vars)
          })
        )

        if (generation !== loadGenerationRef.current) return

        const next: ProjectsFacetLabelLookup = { ...EMPTY_LOOKUP, ready: true }

        results.forEach((result, index) => {
          const spec = LOOKUP_QUERIES[index]
          if (result.status === 'rejected') {
            console.error(`[projects facet lookup] ${spec.key} failed`, result.reason)
            return
          }
          const items = readPath(result.value, [...spec.path])
          ;(next as any)[spec.key] = toLabelMap(items, spec.labelKey)
        })

        if (process.env.NODE_ENV === 'development') {
          const counts = LOOKUP_QUERIES.map(
            ({ key }) => `${key}=${Object.keys((next as any)[key] || {}).length}`
          )
          console.info('[projects facet lookup] loaded', counts.join(', '))
        }

        setLookup(next)
      } catch (err) {
        if (generation !== loadGenerationRef.current) return
        console.error('[projects facet lookup] failed to load reference data', err)
        setLookup({ ...EMPTY_LOOKUP, ready: true })
      }
    }

    load()
  }, [tenantId, effectiveClientId, isAuthenticated, authLoading, apolloClient])

  const value = useMemo(() => lookup, [lookup])

  return (
    <ProjectsFacetLabelLookupContext.Provider value={value}>
      {children}
    </ProjectsFacetLabelLookupContext.Provider>
  )
}

export function useProjectsFacetLabelLookup() {
  return useContext(ProjectsFacetLabelLookupContext)
}
